import React, { useState, useMemo, useContext, useEffect } from "react";
import { Redirect, useParams, useLocation, useHistory } from "react-router-dom";
import { 
    Grid, 
    Typography,
    Box, 
    Button, 
    Tooltip, 
    makeStyles,
    IconButton,
    Fab,
    Checkbox
} from "@material-ui/core";
import FileDownloadIcon from "@material-ui/icons/GetApp";
import CancelScheduleSendIcon from '@material-ui/icons/CancelScheduleSend';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import PostAddIcon from '@material-ui/icons/PostAdd';
import UndoIcon from '@material-ui/icons/Undo';
import AddShoppingCartIcon from '@material-ui/icons/AddShoppingCart';
import ConfirmPopup from "../../components/ConfirmationPopup";
import DateFormat, {formatDate} from "../../components/DateFormat";
import { stringToDatePlain } from '../../components/DateFormat';

import Pagination from "../../components/Pagination";
import Surface from "../../components/Surface";
import InformativeCard from "../../components/InformativeCard";
import Gridable from "../../components/Gridable";
import { ErrorSnackbar, SuccessSnackbar } from "../../components/Snackbars";
import { AppContext } from "../../context/AppContext";
import { useIssuerDetails } from "./useIssuerDetails";
import translate from "../../i18n/Translator";
import TaxBreakdownModal from "../modalboxes/TaxBreakdownModal";
import { useSimpleTaxBreakdownModal } from "../modalboxes/useTaxBreakdownModal";
import {
    IssuerDetailsFilterRequest,
    IssuerInvoiceItemResponse,
    NonIntegratedCfdiParams, RequestCancellationParams
} from "../../model/CfdisNonIntegrated";
import {exportExcel, requestCancellation, revokeCancellationRequest} from "../../api/NonIntegratedCfdisApi";
import ConnectorObjectExporter from "../../connectors/ConnectorObjectExporter";
import {Family} from "../../model/Connector";
import { onAddProvider } from "../nonIntegratedCfdisUtils";
import CancellationRequestPopup from "../CancellationRequestPopup";
import {addCompanyToProvider} from "../../api/ProviderAPI";
import CustomBackdrop from "../../components/CustomBackdrop";

const useStyles = makeStyles((theme) => ({
    root: {
    },
    paginationWrapper: {
        position: 'relative',
    },
    overrideAvatar: {
    '& .MuiAvatar-root': {
      backgroundColor: 'transparent !important',
    }
  },
    headerFilters: {
        position: 'absolute',
        top: theme.spacing(1),
        right: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        zIndex: 1,
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        fontFamily: 'monospace'
    },
    cardsContainer: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2)
    },
    gridContainer: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    loadingContainer: {
        textAlign: 'center',
        padding: theme.spacing(4)
    },
    errorContainer: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.error.main
    },
    gridCell: {
        padding: theme.spacing(1, 2) + ' !important', 
        textAlign: "left",
    },
    rfcReceptorCell: {
        fontFamily: "monospace",
        fontSize: "0.875rem",
    },
    serieFolioCell: {
        fontFamily: "monospace",
        fontSize: "0.875rem",
        fontWeight: 500,
        whiteSpace: "normal", 
        wordBreak: "break-all", 
        lineHeight: 1.2, 
    },
    businessNameCell: {
        fontSize: "0.875rem",
        maxWidth: 180,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        width: "100%", 
        display: "block",
    },
    columnHeaderCell: {
        fontSize: "0.875rem",
        fontWeight: 600,
        maxWidth: 100,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "block",
    },
    columnHeaderWordBreak: {
        fontSize: "0.875rem", 
        fontWeight: 600,
        overflow: "hidden",
        display: "-webkit-box",
        WebkitLineClamp: 3, 
        WebkitBoxOrient: "vertical",
        lineHeight: 1.2,
        maxHeight: "3.4em",
        wordBreak: "break-word",
    },
    rfcCell: {
        fontFamily: "monospace",
        fontSize: "0.875rem",
        maxWidth: 100,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        width: "100%", 
        display: "block",
    },
    dateCell: {
        fontSize: "0.875rem",
        maxWidth: 100,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        width: "100%", 
        display: "block",
    },
    usoCfdiCell: {
        fontSize: "0.875rem",
        maxWidth: 100,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        width: "100%", 
        display: "block",
    },
    amountCell: {
        fontWeight: 500,
        textAlign: "right",
        fontFamily: "monospace",
    },
    actionIcon: {
        fontSize: '1.2rem',
        cursor: 'pointer',
        margin: theme.spacing(0, 0.5),
        color: theme.palette.action.active,
        transition: 'color 0.2s ease',
        '&:hover': {
            color: theme.palette.primary.main,
        }
    },
    disabledActionIcon: {
        fontSize: '1.2rem',
        margin: theme.spacing(0, 0.5),
        color: theme.palette.action.disabled,
        cursor: 'not-allowed',
    },
    actionsCell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(0.1),
    },
    multiSelectHeader: {
        padding: theme.spacing(1, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: theme.palette.grey[50],
    },
    selectedCount: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
    },
    gridRoot: {
        "& .GridableRow": {
            transition: 'background-color 0.2s ease',
        },
        "& .BackgroundRow": {
            backgroundColor: "#fff3cd !important",
            borderLeft: "4px solid #ff9800 !important",
            "&:hover": {
                backgroundColor: "#ffeaa7 !important"
            }
        }
    },
}));

interface RouteParams {
    issuerRfc: string;
}

export default function IssuerDetailsView() {
    const classes = useStyles();
    const context = useContext(AppContext);
    const history = useHistory();
    const { issuerRfc, providerId } = useParams<RouteParams>();
    const location = useLocation();
    
    const tenantId = context.session!.tenant!.id;
    const [canRead] = useState(context.isGranted("NonIntegratedCfdisRead"));
    const [canLoadInvoices] = useState(context.isGrantedAny(["AdminCFDIsCreate", "ReceptorCFDIsCreate", "AddCfdi",
    "ProviderCFDIsCreate", "SenderCFDIsCreate", "CxpCFDIsCreate"]
    ));
    const [canCreateProviders] = useState(context.isGranted("ProvidersCreate"));
    const [canUpdateProviders] = useState(context.isGranted("ProvidersUpdate"));
    
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();
    const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [gridPage, setGridPage] = useState<number>(0);
    const [gridPageSize, setGridPageSize] = useState<number>(20);
    const [redirect, setRedirect] = useState<string | undefined>();

    const filtersFromURL = useMemo(() => {
        const getDefaultFilters = (): IssuerDetailsFilterRequest => {
            const today = new Date();
            const firstDayOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            
            return {
                paymentMethod: 'PUE',
                startDate: firstDayOfPreviousMonth,
                endDate: today,
                page: gridPage,
                pageSize: gridPageSize
            };
        };

        const params = new URLSearchParams(location.search);
        
        const savedFilters = localStorage.getItem('unloadedInvoicesFilters');
        let defaultFilters = getDefaultFilters();
        
        if (savedFilters) {
            try {
                const parsed = JSON.parse(savedFilters);
                defaultFilters = {
                    paymentMethod: parsed.paymentMethod || 'PUE',
                    startDate: new Date(parsed.startDate),
                    endDate: new Date(parsed.endDate),
                    page: gridPage,
                    pageSize: gridPageSize
                };
            } catch {
                defaultFilters = getDefaultFilters();
            }
        }

        if (params.get('paymentMethod') || params.get('startDate') || params.get('endDate')) {
            const parseDate = (dateStr: string | null): Date => {
                if (!dateStr) return defaultFilters.startDate;
                
                const parts = dateStr.split('/');
                if (parts.length !== 3) return defaultFilters.startDate;
                
                const [day, month, year] = parts.map(Number);
                const date = new Date(year, month - 1, day);
                
                if (isNaN(date.getTime())) {
                    console.warn('Invalid date from URL:', dateStr);
                    return defaultFilters.startDate;
                }
                
                return date;
            };

            return {
                paymentMethod: (params.get('paymentMethod') as "PUE" | "PPD") || defaultFilters.paymentMethod,
                startDate: parseDate(params.get('startDate')),
                endDate: parseDate(params.get('endDate')),
                page: gridPage,
                pageSize: gridPageSize
            };
        }

        return defaultFilters;
    }, [location.search, gridPage, gridPageSize]);

    const {
        is_open: isBreakdownModalOpen,
        modal_data: breakdownModalData,
        open_modal: openBreakdownModal,
        close_modal: closeBreakdownModal
    } = useSimpleTaxBreakdownModal();

    const [openExportExcel, setOpenExportExcel] = useState<boolean>(false);
    const [showCancellationRequestPopup, setShowCancellationRequestPopup] = useState<boolean>(false);
    const [openCloseSuccessDialog, setOpenCloseSuccessDialog] = useState<boolean>(false);
    const [companyConfirmationDialog, setCompanyConfirmationDialog] = useState(false);

    const [cancellationParams, setCancellationParams] = useState<RequestCancellationParams>({
        reason: "",
        provider_id: issuerRfc,
        ids: []
    } as RequestCancellationParams);
    const [currentSerieFolio, setCurrentSerieFolio] = useState<string>("");
    const [currentCfdiId, setCurrentCfdiId] = useState<string>("");
    const [currentCompany, setCurrentCompany] = useState<string>("");

    const {
        data, 
        loading, 
        error: dataError,
        refetch 
    } = useIssuerDetails(tenantId, issuerRfc, canRead ? filtersFromURL : null);

    const formatDateForURL = (date: Date): string => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const isRegisteredProvider = data?.issuer_info?.is_registered_provider || false;
    const invoicesData = data?.invoices_grid?.data || [];
    const totalElements = data?.invoices_grid?.total_elements || 0;
    const totalPages = data?.invoices_grid?.total_pages || 0;

    const addProvider = () => {
        const companies = data?.invoices_grid.data.map(
            (invoice: IssuerInvoiceItemResponse) => invoice.rfc_receptor);
        onAddProvider({
            razon_social:data?.issuer_info?.business_name,
            rfc_emisor: issuerRfc,
            rfc_receptor : companies
        }, history);
    };

    const onDownloadExcel = () => {
        setOpenExportExcel(true);
    };

    const onDownloadWithConnector = (connectorId: string) => {
        setOpenExportExcel(false);
        let params = {
            connectorId: "",
            startDate: filtersFromURL.startDate,
            endDate: filtersFromURL.endDate,
            paymentMethod: filtersFromURL.paymentMethod,
            issuerRfc: isRegisteredProvider ? null : issuerRfc,
            providerId: isRegisteredProvider ? providerId : null
        } as NonIntegratedCfdiParams;
        params.connectorId = connectorId;
        exportExcel(context.session!.tenant!.id, params).then((response) => {
            if (response.url) {
                window.open(response.url, "_blank")
            } else {
                setSuccess(translate("cfdis.cfdis_non_integrated.email_export") as string);
            }
        }).catch((error) => {
            setError(error.message);
        });
    }

    const onCloseExporter = () => {
        setOpenExportExcel(false);
    }

    const onSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const selectableInvoices = invoicesData
                .filter(invoice => !invoice.is_cancellation_requested)
                .map(invoice => invoice.id);
            setSelectedInvoices(new Set(selectableInvoices));
        } else {
            setSelectedInvoices(new Set());
        }
    };

    const onSelectInvoiceChange = (invoiceId: string) => {
        const newSelected = new Set(selectedInvoices);
        if (newSelected.has(invoiceId)) {
            newSelected.delete(invoiceId);
        } else {
            newSelected.add(invoiceId);
        }
        if(newSelected.size === 1){
            invoicesData.filter((invoice: IssuerInvoiceItemResponse) => invoice.id === newSelected.values().next().value)
                .map((invoice: IssuerInvoiceItemResponse) => setCurrentSerieFolio(invoice.serie_folio));
        }
        setSelectedInvoices(newSelected);
    };

    const onLoadInvoice = (nonRelatedCfdi: IssuerInvoiceItemResponse) => {
        let uuid = nonRelatedCfdi.uuid;
        let isProvider = context.isGrantedAny(["ProviderCFDIsCreate", "SenderCFDIsCreate"]);
        let isReceptor = context.isGranted("ReceptorCFDIsCreate");

        let mode = isProvider ? "provider" : (isReceptor ? "receptor" : "admin");

        let url = window.location.pathname;
        history.push(url);
        setRedirect(`/non-integrated-cfdi/${uuid}/${mode}/upload`);
    };

    const onRequestCancellationAll = () => {
        setSelectedInvoices(new Set());
        setCancellationParams({
            provider_id: providerId,
            ids: [],
            reason: ""
        } as RequestCancellationParams);
        setShowCancellationRequestPopup(true);
    };

    const onRequestCancellationSelected = () => {
        setCancellationParams({
            provider_id: providerId,
            ids: Array.from(selectedInvoices),
            reason: ""
        } as RequestCancellationParams);
        setShowCancellationRequestPopup(true);
    };

    const onRequestCancellation = (invoice: IssuerInvoiceItemResponse) => {
        setSelectedInvoices(new Set());
        setCurrentSerieFolio(invoice.serie_folio);
        setCancellationParams({
            provider_id: providerId,
            ids: [invoice.id],
            reason: ""
        } as RequestCancellationParams);
        setShowCancellationRequestPopup(true);
    };

    const onConfirmRequestCancellation = (params: RequestCancellationParams)=>{
        setShowCancellationRequestPopup(false);
        setSubmitting(true);
        requestCancellation(context.session!.tenant!.id, params).then((response) => {
            setSuccess(translate("cfdis.cfdis_non_integrated.messages.request_cancellation_success") as string);
            setSelectedInvoices(new Set());
            refetch();
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setSubmitting(false);
        });
    }

    const onRevokeCancellation = (invoice: IssuerInvoiceItemResponse) => {
        setOpenCloseSuccessDialog(true);
        setCurrentCfdiId(invoice.id);
    };

    const onConfirmRevokeCancellation = ()=>{
        if(currentCfdiId === undefined || currentCfdiId === ""){
            return;
        }
        setOpenCloseSuccessDialog(false);
        setSubmitting(true);
        revokeCancellationRequest(context.session!.tenant!.id, currentCfdiId).then(() => {
            setSuccess(translate("cfdis.cfdis_non_integrated.messages.revoke_cancellation_success") as string);
            refetch();
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setSubmitting(false);
        });
    }

    const onConfirmAddCompanyToProvider = () => {
        setCompanyConfirmationDialog(false);
        setSubmitting(true);
        addCompanyToProvider(context.session!.tenant!.id, providerId, currentCompany).then(() => {
            setSuccess(translate("cfdis.cfdis_non_integrated.messages.add_company_to_provider_success",
                {"rfc": currentCompany, "providerName": data?.issuer_info?.business_name}) as string);
            refetch();
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const onAddCompanyToProvider = (invoice: IssuerInvoiceItemResponse) => {
        setCurrentCompany(invoice.rfc_receptor);
        setCompanyConfirmationDialog(true);
    };

    const onChangedPage = (page: number) => {
        setGridPage(page);
        setSelectedInvoices(new Set());
    };

    const onChangedPageSize = (pageSize: number) => {
        setGridPageSize(pageSize);
        setGridPage(0);
        setSelectedInvoices(new Set()); 
    };

    const onViewTrasladosBreakdown = () => {
        openBreakdownModal(
            'transfers',
            'issuer',
            data?.transfers_breakdown,
            filtersFromURL.paymentMethod,
            formatDateForURL(filtersFromURL.startDate),
            formatDateForURL(filtersFromURL.endDate),
            data?.issuer_info?.business_name
        );
    };

    const onViewRetencionesBreakdown = () => {
        openBreakdownModal(
            'withholdings',
            'issuer',
            data?.retentions_breakdown,
            filtersFromURL.paymentMethod,
            formatDateForURL(filtersFromURL.startDate),
            formatDateForURL(filtersFromURL.endDate),
            data?.issuer_info?.business_name
        );
    };

    const onCloseSnackbars = () => {
        setSuccess(undefined);
        setError(undefined);
    };

    const pageTitle = useMemo(() => {
        if (data?.issuer_info?.business_name) {
            return (
                <div>
                    <div>{data.issuer_info.business_name}</div>
                    <div style={{ fontSize: '0.875rem', fontFamily: 'monospace', opacity: 0.8, fontWeight: 400 }}>
                        {issuerRfc}
                    </div>
                </div>
            );
        }
        return issuerRfc || "Cargando información del emisor...";
    }, [data?.issuer_info?.business_name, issuerRfc]);

    const hasResults = useMemo(() => {
        if (!data?.summary) return false;
        
        const hasTraslados = data.summary.transfers?.amounts && 
            Object.values(data.summary.transfers.amounts).some(amount => (amount ?? 0) > 0);
        
        const hasRetenciones = data.summary.retentions?.amounts && 
            Object.values(data.summary.retentions.amounts).some(amount => (amount ?? 0) > 0);
        
        const hasFacturas = (data.summary.invoices?.count ?? 0) > 0;

        return hasTraslados || hasRetenciones || hasFacturas;
    }, [data?.summary]);

    const selectableInvoicesCount = useMemo(() => {
        return invoicesData.filter(invoice => !invoice.is_cancellation_requested).length;
    }, [invoicesData]);

    const isAllSelected = selectableInvoicesCount > 0 && selectedInvoices.size === selectableInvoicesCount;
    const isIndeterminate = selectedInvoices.size > 0 && selectedInvoices.size < selectableInvoicesCount;

    if (redirect) {
        return (<Redirect to={redirect} />);
    }

    const renderActionButtons = () => {
        if (!data?.issuer_info) return null;

        return (
            <>
                {isRegisteredProvider ? (
                    <Tooltip title={translate("cfdis.cfdis_non_integrated.issuer_details.tooltips.request_cancellation_all") as string}>
                        <Fab 
                            color="primary" 
                            size="small" 
                            onClick={onRequestCancellationAll}
                            disabled={loading || !hasResults}
                        >
                            <CancelScheduleSendIcon />
                        </Fab>
                    </Tooltip>
                ) : (
                    canCreateProviders && (
                        <Tooltip title={translate("cfdis.cfdis_non_integrated.issuer_details.tooltips.add_provider") as string}>
                            <Fab 
                                color="primary" 
                                size="small" 
                                onClick={addProvider}
                                disabled={loading}
                            >
                                <PersonAddIcon />
                            </Fab>
                        </Tooltip>
                    )
                )}
                
                <Grid item xs="auto">
                    <Fab 
                        color="primary" 
                        size="small" 
                        title={translate("cfdis.cfdis_non_integrated.issuer_details.tooltips.download_excel") as string}
                        onClick={onDownloadExcel} 
                        disabled={!canRead || loading || !hasResults}
                    >
                        <FileDownloadIcon />
                    </Fab>
                </Grid>
            </>
        );
    };

    const renderCards = () => {
        if (!data?.summary) return null;

        return (
            <Grid container spacing={2} className={classes.cardsContainer}>
                <Grid item xs={12} sm={6} md={4}>
                    <InformativeCard
                        title={translate("cfdis.cfdis_non_integrated.issuer_details.cards.transfers_total") as string}
                        amounts={data.summary.transfers.amounts}
                        cancellationAmounts={
                            isRegisteredProvider 
                                ? data.summary.transfers.cancellationAmounts 
                                : undefined
                        }
                        hasDiscrepancy={
                            isRegisteredProvider 
                                ? data.summary.transfers.hasDiscrepancy 
                                : false
                        }
                        showBreakdownButton={true}
                        onViewBreakdown={onViewTrasladosBreakdown}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <InformativeCard
                        title={translate("cfdis.cfdis_non_integrated.issuer_details.cards.retentions_total") as string}
                        amounts={data.summary.retentions.amounts}
                        cancellationAmounts={
                            isRegisteredProvider 
                                ? data.summary.retentions.cancellationAmounts 
                                : undefined
                        }
                        hasDiscrepancy={
                            isRegisteredProvider 
                                ? data.summary.retentions.hasDiscrepancy 
                                : false
                        }
                        showBreakdownButton={true}
                        onViewBreakdown={onViewRetencionesBreakdown}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <InformativeCard
                        title={translate("cfdis.cfdis_non_integrated.issuer_details.cards.invoices_total") as string}
                        count={data.summary.invoices.count}
                        cancellationCount={
                            isRegisteredProvider 
                                ? data.summary.invoices.cancellationCount 
                                : undefined
                        }
                        secondaryText={
                            isRegisteredProvider 
                                ? (translate("cfdis.cfdis_non_integrated.cards.cancellation_requests") as string)
                                : undefined
                        }
                        hasDiscrepancy={
                            isRegisteredProvider 
                                ? data.summary.invoices.hasDiscrepancy 
                                : false
                        }
                    />
                </Grid>
            </Grid>
        );
    };

    const renderMultiSelectHeader = () => {
        if (!isRegisteredProvider || invoicesData.length === 0) return null;

        return (
            <Box className={classes.multiSelectHeader}>
                <Box display="flex" alignItems="center">
                    <Checkbox
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                        onChange={onSelectAllChange}
                        disabled={selectableInvoicesCount === 0}
                    />
                    <Typography variant="body2" className={classes.selectedCount}>
                        {selectedInvoices.size > 0 
                            ? translate("cfdis.cfdis_non_integrated.messages.selected_invoices", { count: selectedInvoices.size })
                            : translate("cfdis.cfdis_non_integrated.messages.select_all_invoices")
                        }
                    </Typography>
                </Box>
                
                <Button
                    color="primary"
                    size="small"
                    startIcon={<CancelScheduleSendIcon />}
                    onClick={onRequestCancellationSelected}
                    disabled={selectedInvoices.size === 0}
                >
                    {translate("cfdis.cfdis_non_integrated.actions.request_cancellation") as string}
                </Button>
            </Box>
        );
    };

    const renderInvoicesGrid = () => {
        if (!data?.invoices_grid) return null;

        return (
            <Box className={classes.gridContainer}>
                <Box px={2} pb={2}>
                    <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 8 }}>
                        {translate("cfdis.cfdis_non_integrated.issuer_details.grid.title") as string}
                    </Typography>
                </Box>

                {renderMultiSelectHeader()}

                <Box className={classes.gridRoot}>
                    <Gridable
                        items={invoicesData}
                        loading={loading}
                        error={dataError || undefined}
                        empty={translate("cfdis.cfdis_non_integrated.messages.no_invoices_found") as string}
                        onClick={undefined}
                        background={(invoice: IssuerInvoiceItemResponse) => invoice.is_cancellation_requested}
                        columns={[
                            {
                                title: translate("cfdis.cfdis_non_integrated.columns.series_folio") as string,
                                converter: (invoice: IssuerInvoiceItemResponse) => {
                                    const dateObj = typeof invoice.fecha === 'string' 
                                        ? stringToDatePlain(invoice.fecha)
                                        : invoice.fecha;
                                    const formattedDate = dateObj ? formatDate(dateObj, "DD/MM/yyyy") : "-";
                                    
                                    return (
                                        <Box display="flex" alignItems="center">
                                            {isRegisteredProvider && (
                                                <Checkbox
                                                    checked={selectedInvoices.has(invoice.id)}
                                                    onChange={() => onSelectInvoiceChange(invoice.id)}
                                                    disabled={invoice.is_cancellation_requested}
                                                    size="small"
                                                    style={{ marginRight: 8 }}
                                                />
                                            )}
                                            <Box>
                                                <div className={classes.serieFolioCell}>
                                                    {invoice.serie_folio || "-"}
                                                </div>
                                                <div style={{ 
                                                    fontSize: '0.75rem', 
                                                    color: '#666', 
                                                    fontFamily: 'monospace',
                                                    marginTop: '2px'
                                                }}>
                                                    {formattedDate}
                                                </div>
                                            </Box>
                                        </Box>
                                    );
                                },
                                xs: 12 as const, 
                                sm: 3 as const, 
                                md: 2 as const, 
                                lg: 2 as const,
                                className: classes.gridCell
                            },

                            {
                                title: translate("cfdis.cfdis_non_integrated.columns.issuer") as string,
                                converter: (invoice: IssuerInvoiceItemResponse) => {
                                    const businessName = invoice.razon_social_emisor || (translate("cfdis.cfdis_non_integrated.columns.unnamed") as string);
                                    const rfc = invoice.rfc_emisor || "-";
                                    
                                    return (
                                        <Box>
                                            <div style={{ 
                                                fontSize: '0.875rem',
                                                lineHeight: 1.2,
                                                marginBottom: '2px',
                                                wordBreak: 'break-word',
                                                whiteSpace: 'normal'
                                            }}>
                                                {businessName}
                                            </div>
                                            <div style={{ 
                                                fontSize: '0.75rem', 
                                                color: '#666', 
                                                fontFamily: 'monospace',
                                                fontWeight: 500
                                            }}>
                                                {rfc}
                                            </div>
                                        </Box>
                                    );
                                },
                                xs: false as const, 
                                sm: 4 as const, 
                                md: 3 as const, 
                                lg: 3 as const,
                                className: classes.gridCell,
                                fullWidth: true
                            },
                            {
                                title: translate("cfdis.cfdis_non_integrated.columns.receptor") as string,
                                converter: (invoice: IssuerInvoiceItemResponse) => {
                                    const businessName = invoice.razon_social_receptor || (translate("cfdis.cfdis_non_integrated.columns.unnamed") as string);
                                    const rfc = invoice.rfc_receptor || "-";
                                    
                                    return (
                                        <Box>
                                            <div style={{ 
                                                fontSize: '0.875rem',
                                                lineHeight: 1.2,
                                                marginBottom: '2px',
                                                wordBreak: 'break-word',
                                                whiteSpace: 'normal'
                                            }}>
                                                {businessName}
                                            </div>
                                            <div style={{ 
                                                fontSize: '0.75rem', 
                                                color: '#666', 
                                                fontFamily: 'monospace',
                                                fontWeight: 500
                                            }}>
                                                {rfc}
                                            </div>
                                        </Box>
                                    );
                                },
                                xs: false as const, 
                                sm: false as const, 
                                md: 3 as const, 
                                lg: 2 as const,
                                className: classes.gridCell,
                                fullWidth: true
                            },
                            {
                                title: translate("cfdis.cfdis_non_integrated.columns.cfdi_use") as string,
                                converter: (invoice: IssuerInvoiceItemResponse) => {
                                    const cfdiUseKey = invoice.uso_cfdi || "";
                                    const cfdiUseDescription = translate(`reception_rule.cfdi_uses.${cfdiUseKey}`) as string;
                                    const completeCfdiUse = cfdiUseKey && cfdiUseDescription !== `reception_rule.cfdi_uses.${cfdiUseKey}` 
                                        ? `${cfdiUseKey} ${cfdiUseDescription}` 
                                        : cfdiUseKey;

                                    return (
                                        <Tooltip title={completeCfdiUse || ""}>
                                            <span className={classes.usoCfdiCell}>
                                                {completeCfdiUse || "-"}
                                            </span>
                                        </Tooltip>
                                    );
                                },
                                xs: false as const, 
                                sm: 2 as const, 
                                md: 2 as const, 
                                lg: 2 as const,
                                className: classes.gridCell
                            },
                            {
                                title: translate("cfdis.cfdis_non_integrated.columns.amount") as string,
                                converter: (invoice: IssuerInvoiceItemResponse) => (
                                    <span className={classes.amountCell}>
                                        {invoice.monto}
                                    </span>
                                ),
                                xs: false as const, 
                                sm: 3 as const, 
                                md: 1 as const, 
                                lg: 2 as const,
                                justify: "flex-end" as const,
                                className: classes.gridCell
                            },
                            {
                                title: translate("cfdis.cfdis_non_integrated.columns.actions") as string,
                                converter: (invoice: IssuerInvoiceItemResponse) => (
                                    <div className={classes.actionsCell}>
                                        {renderInvoiceActions(invoice)}
                                    </div>
                                ),
                                xs: 12 as const, 
                                sm: 2 as const, 
                                md: 1 as const, 
                                lg: 1 as const,
                                justify: "center" as const,
                                className: classes.gridCell
                            }
                        ]}
                    />
                </Box>
            </Box>
        );
    };

    const renderInvoiceActions = (invoice: IssuerInvoiceItemResponse) => {
        if (isRegisteredProvider) {
            return (
                <>
                    {!invoice.is_cancellation_requested && canLoadInvoices && (
                        <Tooltip title={translate("cfdis.cfdis_non_integrated.tooltips.load_invoice") as string}>
                            <PostAddIcon
                                className={classes.actionIcon}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    onLoadInvoice(invoice);
                                }}
                            />
                        </Tooltip>
                    )}
                    
                    {!invoice.is_cancellation_requested && (
                        <Tooltip title={translate("cfdis.cfdis_non_integrated.tooltips.request_cancellation") as string}>
                            <CancelScheduleSendIcon
                                className={classes.actionIcon}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    onRequestCancellation(invoice);
                                }}
                            />
                        </Tooltip>
                    )}
                    
                    {invoice.is_cancellation_requested && (
                        <Tooltip title={translate("cfdis.cfdis_non_integrated.tooltips.cancel_cancellation_request") as string}>
                            <UndoIcon
                                className={classes.actionIcon}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    onRevokeCancellation(invoice);
                                }}
                            />
                        </Tooltip>
                    )}
                    
                    {(canUpdateProviders && !invoice.is_company_registered) && (
                        <Tooltip title={translate("cfdis.cfdis_non_integrated.tooltips.add_company_to_provider") as string}>
                            <AddShoppingCartIcon
                                className={classes.actionIcon}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    onAddCompanyToProvider(invoice);
                                }}
                            />
                        </Tooltip>
                    )}
                </>
            );
        } else {
            return (
                <Typography variant="caption" color="textSecondary">
                    {translate("cfdis.cfdis_non_integrated.messages.no_actions_available") as string}
                </Typography>
            );
        }
    };

    return (
        <div className={classes.paginationWrapper}>
            <div className={classes.headerFilters}>
                {renderActionButtons()}
            </div>

            <Surface
                title={pageTitle}
                backButton
                backTo={`/comprobantes/facturas-no-cargadas?${new URLSearchParams({
                    paymentMethod: filtersFromURL.paymentMethod,
                    startDate: formatDateForURL(filtersFromURL.startDate),
                    endDate: formatDateForURL(filtersFromURL.endDate)
                })}`}
                icon={<></>}
                className="PaperPagination"
            >
                <Pagination 
                    title="" 
                    icon={<></>} 
                    variant="embeded"
                    page={gridPage + 1} 
                    pageSize={gridPageSize} 
                    count={totalPages} 
                    total={totalElements}
                    onChangedPage={onChangedPage} 
                    onChangedPageSize={onChangedPageSize}
                    hideFooterActions={totalElements === 0}
                >
                    <Box className={classes.root}>
                        {!canRead && (
                            <Box py={4} textAlign="center">
                                <Typography variant="body1" color="textSecondary">
                                    {translate("cfdis.cfdis_non_integrated.messages.no_permissions") as string}
                                </Typography>
                            </Box>
                        )}

                        {canRead && loading && (
                            <Box py={4} textAlign="center">
                                <Typography variant="body1" color="textSecondary">
                                    {translate("cfdis.cfdis_non_integrated.issuer_details.messages.loading") as string}
                                </Typography>
                            </Box>
                        )}

                        {canRead && !loading && (dataError || error) && (
                            <Box className={classes.errorContainer}>
                                <Typography variant="body1">
                                    {dataError || error || (translate("cfdis.cfdis_non_integrated.issuer_details.messages.error_loading") as string)}
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    color="primary" 
                                    onClick={refetch}
                                    style={{ marginTop: 16 }}
                                >
                                    {translate("buttons.retry") as string}
                                </Button>
                            </Box>
                        )}

                        {canRead && !loading && !dataError && !error && data && (
                            <>
                                {renderCards()}
                                {renderInvoicesGrid()}
                            </>
                        )}

                        {openExportExcel && (
                            <ConnectorObjectExporter
                                tenantId={context.session!.tenant!.id}
                                family={Family.NON_INTEGRATED_CFDIS}
                                type={"DATA"}
                                onClose={onCloseExporter}
                                onExport={onDownloadWithConnector}
                            />
                        )}

                        <ErrorSnackbar
                            message={error || dataError} 
                            onClose={onCloseSnackbars} 
                        />
                        <SuccessSnackbar 
                            message={success} 
                            onClose={onCloseSnackbars} 
                        />

                        <CustomBackdrop open={submitting} message={translate("cfdis.processing") as string} />

                        {breakdownModalData && (
                            <TaxBreakdownModal
                                open={isBreakdownModalOpen}
                                type={breakdownModalData.type}
                                context={breakdownModalData.context}
                                data={breakdownModalData.data}
                                paymentMethod={breakdownModalData.payment_method}
                                startDate={breakdownModalData.start_date}
                                endDate={breakdownModalData.end_date}
                                issuerName={breakdownModalData.issuer_name}
                                onClose={closeBreakdownModal}
                            />
                        )}
                    </Box>

                    {showCancellationRequestPopup && (
                        <CancellationRequestPopup
                            open={showCancellationRequestPopup}
                            onClose={() => setShowCancellationRequestPopup(false)}
                            doAction={onConfirmRequestCancellation}
                            providerName={data?.issuer_info.business_name}
                            serieFolio={currentSerieFolio}
                            params={cancellationParams}
                        />
                    )}
                    {openCloseSuccessDialog && (
                        <ConfirmPopup
                            doAction={onConfirmRevokeCancellation}
                            onClose={()=>{setOpenCloseSuccessDialog(false)}}
                            closeText={translate("buttons.close") as string}
                            title={translate("cfdis.cfdis_non_integrated.actions.cancel_cancellation_request") as string}
                            message={translate("cfdis.cfdis_non_integrated.messages.cancellation_confirmed") as string}
                            secondary={translate("") as string}
                            button={translate("buttons.yes_continue") as string} />
                    )}

                    {(companyConfirmationDialog && currentCompany) && (
                        <ConfirmPopup
                            doAction={onConfirmAddCompanyToProvider}
                            onClose={()=>{setCompanyConfirmationDialog(false)}}
                            closeText={translate("buttons.close") as string}
                            title={translate("cfdis.cfdis_non_integrated.actions.add_company_to_provider") as string}
                            message={translate("cfdis.cfdis_non_integrated.messages.company_confirmation",
                                {"rfc":currentCompany}) as string}
                            secondary={translate("") as string}
                            button={translate("buttons.yes_continue") as string} />
                    )}
                </Pagination>
            </Surface>
        </div>
    );
}